/**
 * Spirytale 디자인 토큰 — A안 (양피지와 잉크).
 *
 * 이 파일이 **게시판과 게임을 가르는 경계선**이다. 색·폰트·타이밍·음량은 반드시 여기를
 * 거쳐서 컴포넌트로 흘러간다. Tailwind 클래스는 자유롭게 쓰되, 토큰 밖 색·폰트 사용 금지.
 *
 * 갱신 시 참조: docs/art/art-direction-proposal.md §A.
 */

export const tokens = {
    color: {
        /** 배경 가장 뒤. 먹빛 검정. */
        ink: "#0B0A14",
        /** 양피지. 본문·카드 면의 기본색. */
        parchment: "#F0E4CA",
        /** 바랜 양피지. 보조 텍스트·보더. */
        parchmentDim: "#C7B595",
        /** 장작 불꽃. 강조·대성공·중요 버튼. */
        ember: "#D58542",
        /** 와인. 위험·실패·금기. */
        burgundy: "#6A2940",
        /** 이끼 녹. 성공·안전. */
        moss: "#4A6755",
        /** 황동. 희귀·장비 테두리. */
        brass: "#C59A55",
        /** 강철. UI 보더·중립. */
        steel: "#5A6880",

        /** Rarity 팔레트 토큰 — 백엔드 `rarityDisplay.paletteToken` 과 1:1. */
        rarity: {
            slate: "#5A6880",
            moss: "#4A6755",
            brass: "#C59A55",
            ember: "#D58542",
            burgundy: "#6A2940",
        },

        /** 시맨틱 이벤트 결과색 — outcomeBand 에 매핑. */
        outcome: {
            great: "#D58542",   // GREAT_SUCCESS — ember
            success: "#4A6755", // SUCCESS — moss
            poor: "#C59A55",    // POOR — brass
            failure: "#6A2940", // FAILURE — burgundy
        },
    },

    type: {
        display: "'Noto Serif KR', 'Gowun Batang', serif",
        body: "'Pretendard', system-ui, sans-serif",
        stat: "'JetBrains Mono', ui-monospace, monospace",

        size: {
            // 큰 글씨(hero/title/subtitle) 는 절제, 작은 글씨(body/small/tiny) 는 크게 유지.
            hero: "clamp(56px, 5vw, 80px)",
            title: "clamp(32px, 2.8vw, 44px)",
            subtitle: "clamp(22px, 1.7vw, 30px)",
            body: "clamp(20px, 1.4vw, 26px)",
            small: "20px",
            tiny: "17px",
        },
        lineHeight: { tight: 1.2, snug: 1.4, normal: 1.6, loose: 1.8 },
        weight: { light: 300, regular: 400, medium: 500, semibold: 600, bold: 700 },
        letterSpacing: { tight: "-0.01em", normal: "0", wide: "0.04em" },
    },

    /** 시간 단위는 ms. Framer Motion 의 duration 은 초 단위 — 계산은 `.duration = ms/1000`. */
    timing: {
        /** 씬 진입 페이드·슬라이드. */
        sceneEnter: 600,
        /** 씬 이탈. */
        sceneExit: 400,
        /** BGM 크로스페이드. */
        bgmCrossfade: 1200,
        /** 버튼 prelude (호버·탭 반응). */
        buttonPress: 120,
        /** 타자기 효과의 글자당 간격. */
        dialogueTick: 30,
        /** NarrativeBeat 간 정지. */
        narrativeBeatGap: 400,
        /** 중요 순간의 "숨 한 번" 지연 — 대성공 공개 등. */
        dramaticPause: 900,
    },

    /** Framer Motion 에 그대로 투입 가능한 easing. */
    easing: {
        standard: [0.22, 0.61, 0.36, 1] as const,
        decelerate: [0.05, 0.7, 0.1, 1] as const,
        accelerate: [0.3, 0, 0.8, 0.15] as const,
        sceneCurtain: [0.65, 0, 0.35, 1] as const,
    },

    elevation: {
        sceneBackground: 0,
        sceneContent: 10,
        hud: 40,
        overlay: 60,
        modal: 80,
        splash: 100,
        debug: 200,
    },

    spacing: { xs: 4, s: 8, m: 16, l: 24, xl: 40, xxl: 80 },
    radius: { none: 0, s: 4, m: 8, l: 16, xl: 24, pill: 9999 },

    /** 16:9 고정 설계 기준. 뷰포트가 이 비율에서 벗어나면 letterbox. */
    viewport: {
        designWidth: 1920,
        designHeight: 1080,
        minAspect: 4 / 3,
        maxAspect: 21 / 9,
        letterboxColor: "#04030A",
    },

    audio: {
        defaults: {
            master: 0.8,
            bgm: 0.55,
            sfx: 0.9,
        },
        sfx: {
            buttonTap: "/audio/sfx/ui-tap.mp3",
            buttonHover: "/audio/sfx/ui-hover.mp3",
            buttonConfirm: "/audio/sfx/ui-confirm.mp3",
            pageTurn: "/audio/sfx/page-turn.mp3",
            bookOpen: "/audio/sfx/book-open.mp3",
            codexOpen: "/audio/sfx/codex-open.mp3",
            cardFlip: "/audio/sfx/card-flip.mp3",
            choiceCommit: "/audio/sfx/choice-commit.mp3",
            calendarTick: "/audio/sfx/calendar-tick.mp3",
            inkBloom: "/audio/sfx/ink-bloom.mp3",
            menuSlide: "/audio/sfx/menu-slide.mp3",
            sigilPulse: "/audio/sfx/sigil-pulse.mp3",
            threadWeave: "/audio/sfx/thread-weave.mp3",
            statUp: "/audio/sfx/stat-up.mp3",
            statDown: "/audio/sfx/stat-down.mp3",
            successShimmer: "/audio/sfx/success-shimmer.mp3",
            resultGreat: "/audio/sfx/result-bright-bell.mp3",
            resultSuccess: "/audio/sfx/result-soft.mp3",
            resultPoor: "/audio/sfx/result-uneasy.mp3",
            resultFailure: "/audio/sfx/result-low-string.mp3",
            whisper: "/audio/sfx/whisper.mp3",
            curtainOpen: "/audio/sfx/curtain-open.mp3",
            curtainClose: "/audio/sfx/curtain-close.mp3",

            // 2026-04-23: 전투 전용 SFX 슬롯. 각 slot 은 오디오 파일 경로 — 사용자가 별도
            //   작업 후 rpg-static PVC 로 배포. 파일이 없으면 AudioManager.sfx() 가
            //   조용히 실패하므로 파일 준비 전에도 코드는 안전하게 훅만 박아둔다.
            //
            //   카테고리:
            //   1) 공격/타격 — school 별 1종씩 + 공용 fallback
            //   2) 캐스트 — 시전 시작/완료
            //   3) 상태 변화 — 피격/치명/회피/사망/속박/해제
            //   4) 이동/재배치 — 돌진/순간이동/끌기/밀기
            //
            //   이름 컨벤션: combat.<카테고리>.<세부> — 폴더 depth 깊은 대신 명확.

            // 공격/타격 (school 별)
            combatHitBlade:    "/audio/sfx/combat/hit-blade.mp3",     // 금속성 베기
            combatHitBeast:    "/audio/sfx/combat/hit-beast.mp3",     // 발톱/이빨
            combatHitRune:     "/audio/sfx/combat/hit-rune.mp3",      // 룬 폭발
            combatHitTide:     "/audio/sfx/combat/hit-tide.mp3",      // 물 충돌
            combatHitHex:      "/audio/sfx/combat/hit-hex.mp3",       // 저주 내리침
            combatHitMachine:  "/audio/sfx/combat/hit-machine.mp3",   // 포격/발사
            combatHitHymn:     "/audio/sfx/combat/hit-hymn.mp3",      // 성가 섬광
            combatHitFrost:    "/audio/sfx/combat/hit-frost.mp3",     // 얼음 파열
            combatHitGeneric:  "/audio/sfx/combat/hit-generic.mp3",   // school 미지정 fallback

            // 공격/타격 결과 변주
            combatCrit:        "/audio/sfx/combat/crit.mp3",          // 치명 — 날카로운 고조
            combatMiss:        "/audio/sfx/combat/miss.mp3",          // 빗나감 — 공기 베는 소리
            combatBlock:       "/audio/sfx/combat/block.mp3",         // 방어/가드
            combatDodge:       "/audio/sfx/combat/dodge.mp3",         // 회피

            // 캐스트 (시전)
            combatCastStart:   "/audio/sfx/combat/cast-start.mp3",    // 주문 영창 시작
            combatCastRelease: "/audio/sfx/combat/cast-release.mp3",  // 영창 완료 발동
            combatCastInterrupt: "/audio/sfx/combat/cast-interrupt.mp3", // 시전 차단

            // 상태 변화
            combatStatusApply: "/audio/sfx/combat/status-apply.mp3",  // 일반 표식 부착
            combatStatusBurn:  "/audio/sfx/combat/status-burn.mp3",   // 화상/지속 피해
            combatStatusFreeze:"/audio/sfx/combat/status-freeze.mp3", // 결빙
            combatStatusBind:  "/audio/sfx/combat/status-bind.mp3",   // 속박 사슬
            combatStatusDispel:"/audio/sfx/combat/status-dispel.mp3", // 해제/정화
            combatHeal:        "/audio/sfx/combat/heal.mp3",          // 회복
            combatShield:      "/audio/sfx/combat/shield.mp3",        // 실드 부여

            // 이동/재배치
            combatMovePull:    "/audio/sfx/combat/move-pull.mp3",     // 대상 끌기
            combatMovePush:    "/audio/sfx/combat/move-push.mp3",     // 대상 밀기
            combatMoveDash:    "/audio/sfx/combat/move-dash.mp3",     // 돌진
            combatMoveTeleport:"/audio/sfx/combat/move-teleport.mp3", // 순간이동
            combatMoveFlank:   "/audio/sfx/combat/move-flank.mp3",    // 측면 기동

            // 전투 흐름
            combatEngageStart: "/audio/sfx/combat/engage-start.mp3",  // 전투 개시
            combatDefeat:      "/audio/sfx/combat/defeat.mp3",        // 쓰러짐 (FALL)
            combatVictory:     "/audio/sfx/combat/victory.mp3",       // 승리
            combatDeath:       "/audio/sfx/combat/death.mp3",         // 사망 (플레이어)
        },
    },
} as const;

export type Tokens = typeof tokens;

/**
 * CSS 변수로 펼치는 헬퍼 — index.css 의 :root 에 주입된다.
 * Tailwind 도 이 변수를 참조하도록 설정.
 */
export function tokensAsCssVariables(): Record<string, string> {
    return {
        "--color-ink": tokens.color.ink,
        "--color-parchment": tokens.color.parchment,
        "--color-parchment-dim": tokens.color.parchmentDim,
        "--color-ember": tokens.color.ember,
        "--color-burgundy": tokens.color.burgundy,
        "--color-moss": tokens.color.moss,
        "--color-brass": tokens.color.brass,
        "--color-steel": tokens.color.steel,
        "--color-letterbox": tokens.viewport.letterboxColor,

        "--rarity-slate": tokens.color.rarity.slate,
        "--rarity-moss": tokens.color.rarity.moss,
        "--rarity-brass": tokens.color.rarity.brass,
        "--rarity-ember": tokens.color.rarity.ember,
        "--rarity-burgundy": tokens.color.rarity.burgundy,

        "--outcome-great": tokens.color.outcome.great,
        "--outcome-success": tokens.color.outcome.success,
        "--outcome-poor": tokens.color.outcome.poor,
        "--outcome-failure": tokens.color.outcome.failure,

        "--font-display": tokens.type.display,
        "--font-body": tokens.type.body,
        "--font-stat": tokens.type.stat,
    };
}
